import {Button} from '@/components/ui/button/Button'
import {
    AlertOctagonIcon,
    ChevronDownIcon,
    LayoutAlt03Icon,
    MedicalCrossIcon,
    QueueListIcon,
    SpacingWidth01Icon,
    Trash01Icon
} from '@/components/ui/icon/Icon'
import {Menu, MenuGroup, MenuItem} from '@/components/ui/menu-atoms/MenuAtoms'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {MenuButton, MenuProvider, useDialogStore} from '@ariakit/react'
import {generatePath, Link, useNavigate} from 'react-router-dom'
import {ProjectOptionsSuspendDialog} from '../project-options-suspend-dialog/ProjectOptionsSuspendDialog'
import {ProjectOptionPrintSiarMenuItem} from '@/features/projects/components/project-options-dropdown/ProjectOptionPrintSiarMenuItem'
import {
    ProjectOptionRemoveIncompleteDialog,
    ProjectOptionRemoveIncompleteMenuItem
} from '@/features/projects/components/project-options-remove-incomplete-dialog/ProjectOptionRemoveIncompleteDialog'
import {useState} from 'react'
import {DeleteProjectModal} from '@/features/projects/components/delete-project-modal/DeleteProjectModal'
import {NotAllowedRenewalDialog} from '@/features/projects/components/not-allowed-renewal-dialog/NotAllowedRenewalDialog'
import {ProjectOptionLiftSuspensionDialog} from '@/features/projects/components/project-options-lift-suspension-dialog/ProjectOptionLiftSuspensionDialog'
import {usePermissions} from '@/features/auth/store/store'
import {formatDate} from '@/dayjs'
import {canReopenProject, projectHasActiveOrFutureSuspension, projectHasBeenOperatedOn} from '@/features/projects/utils'
import {ProjectSuspensionsHistoryDialog} from '../project-suspensions-history-dialog/ProjectSuspensionsHistoryDialog'

export const ProjectOptionsDropdown = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const {id: projectId, treatmentRequestId} = project
    const permissions = usePermissions()
    // Operation eligibility comes from shared predicates in projects/utils, so this menu,
    // the operation pages and direct-URL access all enforce exactly the same rules.
    const hasSuspensions = projectHasActiveOrFutureSuspension(project)
    // Once a project has been reopened, extended, renewed, or transformed, none of those
    // four actions can be repeated - the menu items must be hidden entirely, not just disabled.
    const hasBeenOperatedOn = projectHasBeenOperatedOn(project)
    const canBeReopened = canReopenProject(project)
    // Owned here so the dialog can be rendered outside the menu: the menu unmounts its content when it
    // hides, and keeping it open behind the dialog overlay would stack the two surfaces.
    const removeIncompleteDialogStore = useDialogStore()

    return (
        <>
            <MenuProvider>
                <MenuButton
                    render={
                        <Button size="md" variant="secondary">
                            {t('commons:manage_project')}
                            <ChevronDownIcon />
                        </Button>
                    }
                />
                <Menu>
                    <MenuGroup>
                        {project.status == 'open' && permissions?.projects.read && permissions?.projects.write && (
                            <>
                                {hasSuspensions ? (
                                    <ProjectOptionLiftSuspensionDialog project={project} />
                                ) : (
                                    <ProjectOptionsSuspendDialog project={project} />
                                )}
                            </>
                        )}

                        <MenuItem
                            disabled={!project.suspensions || project.suspensions.length == 0}
                            render={<ProjectSuspensionsHistoryDialog suspensions={project.suspensions} />}
                        />

                        {project.type == 'continuous' &&
                            !hasBeenOperatedOn &&
                            permissions?.projects.read &&
                            permissions?.projects.write && <RenewProjectMenuItem project={project} />}

                        {canBeReopened && permissions?.projects.read && permissions?.projects.write && (
                            <MenuItem
                                render={
                                    <Link to={generatePath(routes.PROJECT_REOPENING.handle.path, {id: projectId})} />
                                }
                            >
                                <LayoutAlt03Icon />
                                {t('projects:reopen_project')}
                            </MenuItem>
                        )}

                        {project.type == 'cyclic' && permissions?.projects.read && permissions?.projects.write && (
                            <>
                                {!hasBeenOperatedOn && project.status == 'open' && (
                                    <ExtendsProjectMenuItem project={project} hasSuspensions={hasSuspensions} />
                                )}
                                {!hasBeenOperatedOn && (
                                    <MenuItem
                                        disabled={!!(project.status != 'open' || hasSuspensions)}
                                        render={
                                            <Link
                                                to={generatePath(routes.PROJECT_TRANSFORM.handle.path, {id: projectId})}
                                            />
                                        }
                                    >
                                        <LayoutAlt03Icon />
                                        {t('projects:transform_project')}
                                    </MenuItem>
                                )}
                            </>
                        )}
                        {permissions?.medicalRecords?.read && (
                            <MenuItem
                                render={
                                    <Link
                                        to={generatePath(routes.PATIENT_DETAILS_MEDICAL_RECORDS.handle.path, {
                                            id: project.patientId
                                        })}
                                    />
                                }
                            >
                                <MedicalCrossIcon />
                                {t('global_search:medical_record')}
                            </MenuItem>
                        )}
                        {permissions?.agenda.read && permissions?.agenda.write && (
                            <MenuItem
                                render={
                                    <Link
                                        to={
                                            generatePath(routes.AGENDA.path, {
                                                entity: 'patients',
                                                view: 'week',
                                                date: formatDate(new Date(), 'DD-MM-YYYY')
                                            }) + `?category=custom&subjectIds=${project.patientId}`
                                        }
                                    />
                                }
                            >
                                <LayoutAlt03Icon />
                                {t('projects:patient_agenda')}
                            </MenuItem>
                        )}
                        {permissions?.requests?.read && treatmentRequestId && (
                            <MenuItem
                                render={
                                    <Link
                                        to={generatePath(routes.TREATMENT_REQUEST_DETAILS_ROUTE.handle.path, {
                                            id: treatmentRequestId
                                        })}
                                    />
                                }
                            >
                                <QueueListIcon />
                                {t('commons:treatment_request')}
                            </MenuItem>
                        )}
                        {permissions?.projects.read && permissions?.projects.write && (
                            <>
                                <ProjectOptionPrintSiarMenuItem project={project} />
                                <ProjectOptionRemoveIncompleteMenuItem
                                    project={project}
                                    dialogStore={removeIncompleteDialogStore}
                                />
                                {/* <ProjectOptionExportSiarMenuItem project={project} />
                            <ProjectOptionMarkAsTransmittedMenuItem project={project} /> */}
                            </>
                        )}
                    </MenuGroup>
                    {project.status == 'opening' && permissions?.projects.read && permissions?.projects.write ? (
                        <MenuGroup>
                            {project.canDeleteProject ? (
                                <MenuItem render={<DeleteProjectMenuItem project={project} />} />
                            ) : (
                                <DeleteProjectDisabledMenuItem />
                            )}
                        </MenuGroup>
                    ) : (
                        project.status == 'open' &&
                        permissions?.projects.read &&
                        permissions?.projects.write && (
                            <MenuGroup>
                                <MenuItem
                                    disabled={!!project.endDate}
                                    render={
                                        <Link
                                            to={generatePath(routes.PROJECT_EARLY_CLOSING.handle.path, {id: projectId})}
                                        />
                                    }
                                >
                                    <AlertOctagonIcon />

                                    {t('projects:project_early_closing_title')}
                                </MenuItem>
                            </MenuGroup>
                        )
                    )}
                </Menu>
            </MenuProvider>
            <ProjectOptionRemoveIncompleteDialog project={project} dialogStore={removeIncompleteDialogStore} />
        </>
    )
}

// The renew action can be blocked for a few mutually-exclusive reasons. When blocked we keep the
// MenuItem visible but disabled and wrap it in a tooltip that spells out the exact reason. A disabled
// item doesn't emit the pointer events Radix needs, so the span is the hover target and the item is
// pointer-transparent (same pattern as DeleteProjectDisabledMenuItem).
const RenewProjectMenuItem = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const {id: projectId} = project

    const disabledReason =
        project.status === 'opening'
            ? t('projects:renew_project_disabled_opening')
            : project.progressStatus === 'incomplete'
              ? t('projects:renew_project_disabled_incomplete')
              : project.conclusionReason
                ? t('projects:renew_project_disabled_early_closed')
                : null

    // NotAllowedRenewalDialog/Link only accept the props they declare, so when disabled they must not
    // be used as `render` - Ariakit's disabled/style/aria props would be silently dropped and the item
    // would stay clickable. Render a bare, genuinely disabled MenuItem instead.
    const menuItem = disabledReason ? (
        <MenuItem disabled style={{pointerEvents: 'none'}}>
            <LayoutAlt03Icon />
            {t('projects:renew_project_title')}
        </MenuItem>
    ) : (
        <MenuItem
            render={
                project.isRenewable ? (
                    <Link to={generatePath(routes.PROJECT_RENEW.handle.path, {id: projectId})} />
                ) : (
                    <NotAllowedRenewalDialog projectType="continuous" />
                )
            }
        >
            <LayoutAlt03Icon />
            {t('projects:renew_project_title')}
        </MenuItem>
    )

    if (!disabledReason) return menuItem

    return (
        <Tooltip variant="light" content={disabledReason} triggerProps={{asChild: true}}>
            <span style={{display: 'block', cursor: 'not-allowed'}}>{menuItem}</span>
        </Tooltip>
    )
}

// Same blocked-with-tooltip pattern as RenewProjectMenuItem, for the cyclic "extend cycle" action.
const ExtendsProjectMenuItem = ({project, hasSuspensions}: {project: ProjectExtended; hasSuspensions: boolean}) => {
    const {t} = useTranslation()
    const {id: projectId} = project

    const disabledReason =
        project.status === 'opening'
            ? t('projects:extends_project_disabled_opening')
            : project.conclusionReason || project.status === 'closed' || !project.status
              ? t('projects:extends_project_disabled_closed')
              : hasSuspensions
                ? t('projects:extends_project_disabled_suspended')
                : null

    // NotAllowedRenewalDialog/Link only accept the props they declare, so when disabled they must not
    // be used as `render` - Ariakit's disabled/style/aria props would be silently dropped and the item
    // would stay clickable. Render a bare, genuinely disabled MenuItem instead.
    const menuItem = disabledReason ? (
        <MenuItem disabled style={{pointerEvents: 'none'}}>
            <SpacingWidth01Icon />
            {t('projects:extends_project')}
        </MenuItem>
    ) : (
        <MenuItem
            render={
                project.isRenewable ? (
                    <Link to={generatePath(routes.PROJECT_EXTENDS_CYCLE.handle.path, {id: projectId})} />
                ) : (
                    <NotAllowedRenewalDialog projectType="cyclic" />
                )
            }
        >
            <SpacingWidth01Icon />
            {t('projects:extends_project')}
        </MenuItem>
    )

    if (!disabledReason) return menuItem

    return (
        <Tooltip variant="light" content={disabledReason} triggerProps={{asChild: true}}>
            <span style={{display: 'block', cursor: 'not-allowed'}}>{menuItem}</span>
        </Tooltip>
    )
}

const DeleteProjectMenuItem = ({project}: {project: ProjectExtended}) => {
    const {t} = useTranslation()
    const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false)
    const navigate = useNavigate()
    return (
        <>
            <Button onClick={() => setIsDeleteProjectModalOpen(true)} variant="ghostDanger">
                <Trash01Icon />
                {t('commons:delete')}
            </Button>
            {isDeleteProjectModalOpen && (
                <DeleteProjectModal
                    project={project}
                    onClose={() => setIsDeleteProjectModalOpen(false)}
                    onSuccess={() => {
                        setIsDeleteProjectModalOpen(false)
                        navigate(routes.PROJECTS_ROUTE.path)
                    }}
                />
            )}
        </>
    )
}

// Deletion is blocked when the project has linked events. We show a disabled button
// wrapped in a tooltip explaining why. A native disabled button doesn't emit the pointer
// events Radix needs, so the span is the hover target and the button is pointer-transparent.
const DeleteProjectDisabledMenuItem = () => {
    const {t} = useTranslation()
    return (
        <Tooltip variant="light" content={t('projects:delete_project_disabled_tooltip')} triggerProps={{asChild: true}}>
            <span style={{display: 'block', cursor: 'not-allowed'}}>
                <Button disabled variant="ghostDanger" style={{pointerEvents: 'none'}}>
                    <Trash01Icon />
                    {t('commons:delete')}
                </Button>
            </span>
        </Tooltip>
    )
}
